import React, {useEffect, useState} from "react"
// MUI
import {Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography} from "@mui/material"
import {CloseRounded} from "@mui/icons-material"
// Components
import AppEditorPreview from "@/components/Apps/AppsEditor/AppEditorPreview/AppEditorPreview"
// Data
import {useData} from "@/context/PackagingDataProvider"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"

type AppPreviewDialogProps = {
    open: boolean
    onClose: () => void
    // The list-item app ({id, name, ...}); the full app (layout, translations)
    // is fetched on open.
    app: any
    t: (k: string, o?: any) => string
}

// Visual preview of an app straight from the Apps list — fetches the full app
// and renders the same phone preview as the editor, with a language switcher
// when the app has several translations.
export default function AppPreviewDialog(props: AppPreviewDialogProps) {
    const {open, onClose, app, t} = props
    const {getSingleApp} = useData()

    const [fullApp, setFullApp] = useState<any>(null)
    const [previewTranslation, setPreviewTranslation] = useState<any>(null)

    useEffect(() => {
        if (open && app?.id) {
            setFullApp(null)
            setPreviewTranslation(null)
            getSingleApp(app.id).then((res: any) => {
                if (res) {
                    setFullApp(res)
                    const translations = res.translations ?? []
                    setPreviewTranslation(translations.find((tr: any) => tr.default) ?? translations[0] ?? null)
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, app?.id])

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
            <DialogTitle sx={{display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5}}>
                <Box sx={{minWidth: 0}}>
                    <Typography component={"span"} sx={{fontSize: "18px", fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                        {app?.name || t("app_untitled")}
                    </Typography>
                    <Typography component={"span"} sx={{fontSize: "12.5px", color: tk.color.textSecondary}}>
                        {t("app_preview_subtitle")}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label={t("common:close")} sx={{color: tk.color.textTertiary}}>
                    <CloseRounded />
                </IconButton>
            </DialogTitle>
            <DialogContent className={"fancyScroll"}>
                {!fullApp || !fullApp.layout ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {/* Language switcher — only when there is a choice */}
                        {(fullApp.translations?.length ?? 0) > 1 && (
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5}}>
                                {fullApp.translations.map((tr: any) => {
                                    const active = previewTranslation?.id === tr.id
                                    return (
                                        <Box
                                            key={tr.id}
                                            component={"button"}
                                            type={"button"}
                                            onClick={() => setPreviewTranslation(tr)}
                                            sx={(theme) => ({
                                                cursor: "pointer",
                                                px: 1.25, py: "3px",
                                                fontSize: "11.5px", fontWeight: 600,
                                                fontFamily: "inherit",
                                                textTransform: "uppercase",
                                                borderRadius: tk.radius.pill,
                                                border: `1px solid ${active ? theme.palette.primary.main : tk.color.border}`,
                                                color: active ? theme.palette.primary.main : tk.color.textSecondary,
                                                backgroundColor: active ? tk.color.green50 : tk.color.surface,
                                                transition: "all .15s",
                                            })}
                                        >
                                            {tr.language}
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <AppEditorPreview {...{app: fullApp, previewTranslation, t}} />
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
